import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  setDoc,
  deleteDoc,
  serverTimestamp,
  query,
  where,
  limit,
  addDoc,
} from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { useAuth } from "../../context/AuthContext";

/* =========================
   INDUSTRY OPTIONS
========================= */
const INDUSTRIES = [
  "HealthTech",
  "MedTech",
  "BioTech",
  "FinTech",
  "AI",
  "EdTech",
  "AgriTech",
  "ClimateTech",
  "SaaS",
  "DeepTech",
  "Web3",
  "E-commerce",
  "Logistics",
  "Consumer",
  "Enterprise",
  "Gaming",
  "AR/VR",
  "Robotics",
  "IoT",
  "Other",
];

function CohortDetail() {
  const { cohortId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [myName, setMyName] = useState("Evaluator");

  const [cohort, setCohort] = useState(null);

  /* ================= TEAM ================= */
  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteError, setInviteError] = useState("");
  const [inviteSuccess, setInviteSuccess] = useState("");
  const [inviting, setInviting] = useState(false);

  /* ================= STARTUPS ================= */
  const [members, setMembers] = useState([]);
  const [sharedStartups, setSharedStartups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingId, setAddingId] = useState(null);

  const [searchMembers, setSearchMembers] = useState("");
  const [industryMembers, setIndustryMembers] = useState("");
  const [searchAdd, setSearchAdd] = useState("");
  const [industryAdd, setIndustryAdd] = useState("");

  /* ================= VOTING ================= */
  const [votes, setVotes] = useState([]);
  const [myVotes, setMyVotes] = useState({});
  const [voteMessage, setVoteMessage] = useState("");

  /* ================= NOTES ================= */
  const [notes, setNotes] = useState([]);
  const [noteInput, setNoteInput] = useState("");
  const [noteMessage, setNoteMessage] = useState("");

  /* ================= UI COLLAPSE STATE ================= */
  const [openTeam, setOpenTeam] = useState(true);
  const [openStartups, setOpenStartups] = useState(true);
  const [openVoting, setOpenVoting] = useState(true);
  const [openNotes, setOpenNotes] = useState(true);
  const [openAdd, setOpenAdd] = useState(true);

  /* =========================
     ROLE DETECTION (FIXED)
  ========================= */
  const isOwner = !!cohort && user?.uid === cohort.evaluatorId;

  const votingMembers = [
    ...(isOwner
      ? [
          {
            userId: user.uid,
            name: myName,
          },
        ]
      : []),
    ...teamMembers,
  ];

  /* =========================
     LOAD COHORT
  ========================= */
  useEffect(() => {
    const loadCohort = async () => {
      try {
        const snap = await getDoc(doc(db, "cohorts", cohortId));
        if (snap.exists()) {
          setCohort({ id: snap.id, ...snap.data() });
        }
      } catch (err) {
        console.error("Failed to load cohort", err);
      }
    };

    loadCohort();
  }, [cohortId]);

  useEffect(() => {
    if (!user?.uid) return;

    const loadMyName = async () => {
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (snap.exists()) {
          setMyName(snap.data().fullName || "Evaluator");
        }
      } catch (err) {
        console.error("Failed to load evaluator name", err);
      }
    };

    loadMyName();
  }, [user?.uid]);

  /* =========================
     LOAD ACCEPTED TEAM MEMBERS
  ========================= */
  useEffect(() => {
    if (!cohort || !user?.uid) return;

    const loadTeamMembers = async () => {
      try {
        let q;

        if (isOwner) {
          q = query(
            collection(db, "cohortMembers"),
            where("cohortId", "==", cohortId)
          );
        } else {
          q = query(
            collection(db, "cohortMembers"),
            where("cohortId", "==", cohortId),
            where("userId", "==", user.uid)
          );
        }

        const snap = await getDocs(q);

        const rawMembers = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        const userSnaps = await Promise.all(
          rawMembers.map((m) => getDoc(doc(db, "users", m.userId)))
        );

        const enriched = rawMembers.map((m, i) => {
          const u = userSnaps[i];
          return {
            id: m.id,
            userId: m.userId,
            email: u.exists() ? u.data().email : "",
            name: u.exists() ? u.data().fullName : "Evaluator",
          };
        });

        setTeamMembers(enriched);
      } catch (err) {
        console.error("Failed to load team members", err);
      }
    };

    loadTeamMembers();
  }, [cohort, cohortId, user?.uid, isOwner]);

  /* =========================
     LOAD PENDING INVITES (FIXED)
  ========================= */
  useEffect(() => {
    if (!cohort || !user?.uid || cohort.evaluatorId !== user.uid) {
      setPendingInvites([]);
      return;
    }

    const loadInvites = async () => {
      try {
        const snap = await getDocs(
          query(
            collection(db, "cohortInvites"),
            where("cohortId", "==", cohortId),
            where("senderEvaluatorId", "==", user.uid), // ✅ THIS FIXES PERMISSIONS
            where("status", "==", "pending")
          )
        );

        setPendingInvites(
          snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        );
      } catch (err) {
        console.error("Failed to load invites", err);
      }
    };

    loadInvites();
  }, [cohort, cohortId, user?.uid]);
  /* =========================
     SEND INVITE
  ========================= */
  const sendInvite = async () => {
    if (!isOwner) return;

    setInviteError("");
    setInviteSuccess("");

    if (!inviteEmail.trim()) {
      setInviteError("Email is required.");
      return;
    }

    setInviting(true);

    try {
      const userSnap = await getDocs(
        query(
          collection(db, "users"),
          where("email", "==", inviteEmail.trim()),
          limit(1)
        )
      );

      if (userSnap.empty) {
        setInviteError("No evaluator found with this email.");
        return;
      }

      const invitedUser = userSnap.docs[0];
      const invitedData = invitedUser.data();

      if (invitedData.persona !== "evaluator") {
        setInviteError("Only evaluators can be invited.");
        return;
      }

      const senderSnap = await getDoc(doc(db, "users", user.uid));
      const senderName = senderSnap.exists()
        ? senderSnap.data().fullName
        : "Evaluator";

      const inviteRef = await addDoc(collection(db, "cohortInvites"), {
        cohortId,
        cohortName: cohort.name,
        cohortDescription: cohort.description || "",
        invitedEmail: inviteEmail.trim(),
        invitedUserId: invitedUser.id,
        senderEvaluatorId: user.uid,
        senderName,
        status: "pending",
        createdAt: serverTimestamp(),
      });

      setPendingInvites((prev) => [
        ...prev,
        {
          id: inviteRef.id,
          cohortId,
          invitedEmail: inviteEmail.trim(),
          invitedUserId: invitedUser.id,
          senderEvaluatorId: user.uid,
          senderName,
          status: "pending",
        },
      ]);

      setInviteEmail("");
      setInviteSuccess("Invite sent successfully.");
    } catch (err) {
      console.error(err);
      setInviteError("Failed to send invite.");
    } finally {
      setInviting(false);
    }
  };

  /* =========================
     REMOVE TEAM MEMBER
  ========================= */
  const removeTeamMember = async (memberId) => {
    if (!isOwner) return;

    if (!window.confirm("Remove this team member from the cohort?")) return;

    try {
      await deleteDoc(doc(db, "cohortMembers", memberId));
      setTeamMembers((prev) => prev.filter((m) => m.id !== memberId));
    } catch (err) {
      console.error("Failed to remove team member", err);
    }
  };

  /* =========================
     LOAD COHORT STARTUPS
  ========================= */
  useEffect(() => {
    const loadMembers = async () => {
      try {
        const snap = await getDocs(
          query(
            collection(db, "cohorts", cohortId, "members"),
            limit(100)
          )
        );

        const founderIds = snap.docs.map((d) => d.data().founderId);

        const founderSnaps = await Promise.all(
          founderIds.map((id) => getDoc(doc(db, "users", id)))
        );

        const founderMap = {};
        founderSnaps.forEach((s) => {
          if (s.exists()) founderMap[s.id] = s.data();
        });

        const results = snap.docs
          .map((d) => {
            const f = founderMap[d.data().founderId];
            if (!f) return null;
            return {
              id: d.data().founderId,
              founderId: d.data().founderId,
              startupName: d.data().startupName,
              founderName: f.fullName,
              industries: f.industries || [],
            };
          })
          .filter(Boolean);

        setMembers(results);
      } catch (err) {
        console.error("Failed to load cohort startups", err);
      }
    };

    loadMembers();
  }, [cohortId]);

  /* =========================
     LOAD SHARED STARTUPS
  ========================= */
  useEffect(() => {
    if (!user?.uid || !isOwner) {
      setSharedStartups([]);
      setLoading(false);
      return;
    }

    const loadShared = async () => {
      try {
        const snap = await getDocs(
          query(
            collection(db, "shares"),
            where("evaluatorId", "==", user.uid),
            limit(200)
          )
        );

        const founderIds = snap.docs.map((s) => s.data().founderId);

        const founderSnaps = await Promise.all(
          founderIds.map((id) => getDoc(doc(db, "users", id)))
        );

        const results = founderSnaps
          .map((f, i) =>
            f.exists()
              ? {
                  founderId: founderIds[i],
                  startupName: f.data().startupName,
                  founderName: f.data().fullName,
                  industries: f.data().industries || [],
                }
              : null
          )
          .filter(Boolean);

        setSharedStartups(results);
      } catch (err) {
        console.error("Failed to load shared startups", err);
      } finally {
        setLoading(false);
      }
    };

    loadShared();
  }, [user?.uid, isOwner]);

  /* =========================
     ADD STARTUP TO COHORT
  ========================= */
  const addToCohort = async (startup) => {
    if (!isOwner || addingId) return;

    if (members.some((m) => m.founderId === startup.founderId)) {
      alert("This startup is already in the cohort.");
      return;
    }

    try {
      setAddingId(startup.founderId);

      await setDoc(
        doc(db, "cohorts", cohortId, "members", startup.founderId),
        {
          founderId: startup.founderId,
          startupName: startup.startupName,
          addedAt: serverTimestamp(),
        }
      );

      setMembers((prev) => [
        ...prev,
        { ...startup, id: startup.founderId },
      ]);
    } catch {
      alert("Failed to add startup to cohort.");
    } finally {
      setAddingId(null);
    }
  };

  /* =========================
     REMOVE STARTUP FROM COHORT
  ========================= */
  const removeFromCohort = async (founderId) => {
    if (!isOwner) return;

    if (!window.confirm("Remove this startup from cohort?")) return;

    try {
      await deleteDoc(
        doc(db, "cohorts", cohortId, "members", founderId)
      );

      setMembers((prev) =>
        prev.filter((m) => m.founderId !== founderId)
      );
    } catch (err) {
      console.error("Failed to remove startup", err);
    }
  };

  /* =========================
     LOAD COHORT VOTES
  ========================= */
  useEffect(() => {
    if (!user?.uid) return;

    const loadVotes = async () => {
      try {
        const snap = await getDocs(
          query(
            collection(db, "cohortVotes"),
            where("cohortId", "==", cohortId)
          )
        );

        const list = snap.docs.map((d) => d.data());
        setVotes(list);

        const mine = {};
        list
          .filter((v) => v.userId === user.uid)
          .forEach((v) => {
            mine[v.startupId] = v.score;
          });

        setMyVotes(mine);
      } catch (err) {
        console.error("Failed to load cohort votes", err);
      }
    };

    loadVotes();
  }, [cohortId, user?.uid]);

  /* =========================
     LOAD COHORT NOTES
  ========================= */
  useEffect(() => {
    if (!user?.uid) return;

    const loadNotes = async () => {
      try {
        const snap = await getDocs(
          query(
            collection(db, "cohortNotes"),
            where("cohortId", "==", cohortId)
          )
        );

        setNotes(snap.docs.map((d) => d.data()));
      } catch (err) {
        console.error("Failed to load cohort notes", err);
      }
    };

    loadNotes();
  }, [cohortId, user?.uid]);
  /* =========================
     SUBMIT VOTE
  ========================= */
  const submitVote = async (startupId, score) => {
    if (!score) return;
    if (myVotes[startupId]) return;

    try {
      await addDoc(collection(db, "cohortVotes"), {
        cohortId,
        startupId,
        userId: user.uid,
        userName: myName,
        score,
        createdAt: serverTimestamp(),
      });

      setMyVotes((prev) => ({
        ...prev,
        [startupId]: score,
      }));

      setVotes((prev) => [
        ...prev,
        {
          cohortId,
          startupId,
          userId: user.uid,
          userName: myName,
          score,
        },
      ]);

      setVoteMessage("Vote submitted successfully.");
    } catch (err) {
      console.error("Failed to submit vote", err);
      alert("Failed to submit vote.");
    }
  };

  /* =========================
     SUBMIT NOTE
  ========================= */
  const submitNote = async () => {
    if (!noteInput.trim()) return;

    try {
      await addDoc(collection(db, "cohortNotes"), {
        cohortId,
        userId: user.uid,
        userName: myName,
        note: noteInput.trim(),
        createdAt: serverTimestamp(),
      });

      setNotes((prev) => [
        ...prev,
        {
          userName: myName,
          note: noteInput.trim(),
        },
      ]);

      setNoteInput("");
      setNoteMessage("Note added successfully.");
    } catch (err) {
      console.error("Failed to submit note", err);
      alert("Failed to submit note.");
    }
  };

  /* =========================
     TEAM STATISTICS
  ========================= */
  const getStartupMean = (startupId) => {
    const startupVotes = votes.filter(
      (v) => v.startupId === startupId
    );

    if (startupVotes.length === 0) return "-";

    const avg =
      startupVotes.reduce((sum, v) => sum + v.score, 0) /
      startupVotes.length;

    return avg.toFixed(2);
  };

  const allScores = votes.map((v) => v.score);

  const mean =
    allScores.length > 0
      ? (
          allScores.reduce((sum, s) => sum + s, 0) /
          allScores.length
        ).toFixed(2)
      : "-";

  const variance =
    allScores.length > 0
      ? (
          allScores.reduce(
            (sum, s) => sum + Math.pow(s - mean, 2),
            0
          ) / allScores.length
        ).toFixed(2)
      : "-";

  const stdDeviation =
    variance !== "-" ? Math.sqrt(variance).toFixed(2) : "-";

  let insight = "No data yet";

  if (stdDeviation !== "-") {
    const sd = Number(stdDeviation);

    if (sd <= 0.5) {
      insight = "High agreement among evaluators";
    } else if (sd <= 1.0) {
      insight = "Moderate agreement with some differences";
    } else {
      insight = "Wide disagreement in evaluations";
    }
  };

  if (loading) return <p>Loading cohort…</p>;
  if (!cohort) return <p>Cohort not found.</p>;

  const memberFounderIds = members.map((m) => m.founderId);

  const filteredMembers = members.filter((m) => {
    const q = searchMembers.toLowerCase();
    return (
      (!q ||
        m.startupName.toLowerCase().includes(q) ||
        m.founderName.toLowerCase().includes(q)) &&
      (!industryMembers || m.industries.includes(industryMembers))
    );
  });

  const filteredAdd = isOwner
    ? sharedStartups
        .filter((s) => !memberFounderIds.includes(s.founderId))
        .filter((s) => {
          const q = searchAdd.toLowerCase();
          return (
            (!q ||
              s.startupName.toLowerCase().includes(q) ||
              s.founderName.toLowerCase().includes(q)) &&
            (!industryAdd || s.industries.includes(industryAdd))
          );
        })
    : [];
  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", paddingBottom: 60 }}>
      {/* ===== HEADER ===== */}
      <div
        style={{
          background: "linear-gradient(135deg, #1e293b, #0f172a)",
          borderRadius: 16,
          padding: "28px 32px",
          color: "white",
          marginBottom: 28,
          boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
        }}
      >
        <button
          className="btn-secondary"
          onClick={() => navigate("/evaluator/cohorts")}
          style={{ marginBottom: 16 }}
        >
          ← Back to cohorts
        </button>

        <h1 style={{ fontSize: 28, fontWeight: 700 }}>{cohort.name}</h1>
        <p style={{ opacity: 0.85, marginTop: 6 }}>
          {cohort.description || "Private evaluation cohort"}
        </p>
      </div>

      {/* ================= TEAM ================= */}
      <details open className="card" style={{ marginBottom: 24 }}>
        <summary style={{ padding: 24, cursor: "pointer", fontWeight: 600 }}>
          Cohort Team
        </summary>

        <div style={{ padding: 24, borderTop: "1px solid #e5e7eb", maxHeight: 320, overflowY: "auto" }}>
          {isOwner && (
            <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
              <input
                type="email"
                placeholder="Invite evaluator by email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                style={{ flex: 1, minWidth: 260 }}
              />
              <button className="btn-primary" onClick={sendInvite} disabled={inviting}>
                {inviting ? "Sending…" : "Send Invite"}
              </button>
            </div>
          )}

          {inviteError && <p style={{ color: "red" }}>{inviteError}</p>}
          {inviteSuccess && <p style={{ color: "green" }}>{inviteSuccess}</p>}

          {/* ===== TEAM MEMBERS ===== */}
          {teamMembers.length === 0 ? (
            <p style={{ color: "#64748b" }}>No team members yet.</p>
          ) : (
            teamMembers.map((m) => (
              <div
                key={m.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "10px 0",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <span>
                  <strong>{m.name}</strong> – {m.email}
                </span>
                {isOwner && (
                  <button
                    className="btn-secondary"
                    onClick={() => removeTeamMember(m.id)}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))
          )}

          {/* ===== PENDING INVITES (OWNER ONLY) ===== */}
          {isOwner && (
            <div style={{ marginTop: 24 }}>
              <h4 style={{ marginBottom: 8 }}>Pending Invites</h4>

              {pendingInvites.length === 0 ? (
                <p style={{ color: "#64748b" }}>No pending invites.</p>
              ) : (
                pendingInvites.map((invite) => (
                  <div
                    key={invite.id}
                    style={{
                      padding: "8px 0",
                      borderBottom: "1px solid #e5e7eb",
                      fontSize: 14,
                    }}
                  >
                    {invite.invitedEmail}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </details>

      {/* ================= STARTUPS ================= */}
      <details open className="card" style={{ marginBottom: 24 }}>
        <summary style={{ padding: 24, cursor: "pointer", fontWeight: 600 }}>
          Startups in this cohort
        </summary>

        <div style={{ padding: 24, borderTop: "1px solid #e5e7eb", maxHeight: 420, overflowY: "auto" }}>
          {filteredMembers.length === 0 ? (
            <p style={{ color: "#64748b" }}>No startups in this cohort.</p>
          ) : (
            filteredMembers.map((m) => (
              <div
                key={m.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "14px 0",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{m.startupName}</div>
                  <div style={{ fontSize: 13, color: "#64748b" }}>
                    Founder: {m.founderName}
                  </div>
                </div>

                <div>
                  <button
                    className="btn-secondary"
                    onClick={() => navigate(`/evaluator/startup/${m.founderId}`)}
                  >
                    View
                  </button>

                  {isOwner && (
                    <button
                      className="btn-secondary"
                      style={{ marginLeft: 8 }}
                      onClick={() => removeFromCohort(m.founderId)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </details>

      {/* ================= VOTING ================= */}
      <details open className="card" style={{ marginBottom: 24 }}>
        <summary style={{ padding: 24, cursor: "pointer", fontWeight: 600 }}>
          Cohort Voting & Statistics
        </summary>

        <div style={{ padding: 24, borderTop: "1px solid #e5e7eb", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ background: "#f1f5f9" }}>
              <tr>
                <th align="left" style={{ padding: 10 }}>Startup</th>
                {votingMembers.map((m) => (
                  <th key={m.userId} align="center" style={{ padding: 10 }}>{m.name}</th>
                ))}
                <th align="center" style={{ padding: 10 }}>Mean</th>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.map((s) => (
                <tr key={s.founderId} style={{ borderBottom: "1px solid #e5e7eb" }}>
                  <td style={{ padding: 10 }}>{s.startupName}</td>

                  {votingMembers.map((m) => {
                    const existing = votes.find(
                      (v) => v.startupId === s.founderId && v.userId === m.userId
                    );

                    if (existing) return <td key={m.userId} align="center">{existing.score}</td>;
                    if (m.userId !== user.uid) return <td key={m.userId} align="center">–</td>;

                    return (
                      <td key={m.userId} align="center">
                        <select
                          disabled={!!myVotes[s.founderId]}
                          onChange={(e) => submitVote(s.founderId, Number(e.target.value))}
                        >
                          <option value="">Select</option>
                          {[1, 2, 3, 4, 5].map((n) => (
                            <option key={n} value={n}>{n}</option>
                          ))}
                        </select>
                      </td>
                    );
                  })}

                  <td align="center" style={{ fontWeight: 600 }}>
                    {getStartupMean(s.founderId)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {voteMessage && <p style={{ color: "green", marginTop: 12 }}>{voteMessage}</p>}

          <div style={{ marginTop: 20, fontSize: 14, color: "#475569" }}>
            <p><strong>Mean Score:</strong> {mean}</p>
            <p><strong>Variance:</strong> {variance}</p>
            <p><strong>Standard Deviation:</strong> {stdDeviation}</p>
            <p><strong>Insight:</strong> {insight}</p>
          </div>
        </div>
      </details>

      {/* ================= NOTES ================= */}
      <details open className="card" style={{ marginBottom: 24 }}>
        <summary style={{ padding: 24, cursor: "pointer", fontWeight: 600 }}>
          Discussion Notes
        </summary>

        <div style={{ padding: 24, borderTop: "1px solid #e5e7eb", maxHeight: 320, overflowY: "auto" }}>
          <textarea
            value={noteInput}
            onChange={(e) => setNoteInput(e.target.value)}
            rows={3}
            style={{ width: "100%", marginTop: 12 }}
            placeholder="Leave a note for the cohort…"
          />

          <button className="btn-primary" onClick={submitNote} style={{ marginTop: 10 }}>
            Add Note
          </button>

          {noteMessage && <p style={{ color: "green", marginTop: 8 }}>{noteMessage}</p>}

          <div style={{ marginTop: 16 }}>
            {notes.map((n, i) => (
              <div
                key={i}
                style={{
                  padding: 10,
                  background: "#f8fafc",
                  borderRadius: 8,
                  marginBottom: 8,
                }}
              >
                <strong>{n.userName}:</strong> {n.note}
              </div>
            ))}
          </div>
        </div>
      </details>

      {/* ================= ADD STARTUP ================= */}
      {isOwner && (
        <details className="card">
          <summary style={{ padding: 24, cursor: "pointer", fontWeight: 600 }}>
            Add startup to cohort
          </summary>

          <div style={{ padding: 24, borderTop: "1px solid #e5e7eb", maxHeight: 360, overflowY: "auto" }}>
            {filteredAdd.length === 0 ? (
              <p style={{ color: "#64748b" }}>No startups available to add.</p>
            ) : (
              filteredAdd.map((s) => (
                <div
                  key={s.founderId}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "12px 0",
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <strong>{s.startupName}</strong>
                  <button
                    className="btn-primary"
                    disabled={addingId === s.founderId}
                    onClick={() => addToCohort(s)}
                  >
                    {addingId === s.founderId ? "Adding…" : "Add"}
                  </button>
                </div>
              ))
            )}
          </div>
        </details>
      )}
    </div>
  );
}

export default CohortDetail;
